import { useEffect, useRef } from "react";

import { IconSize } from "@/DesignSystem/Atoms/Icons/Icon/Icon";

import styles from "../Checkbox.module.scss";

interface ICheckboxProps {
    /** Whether the checkbox is checked. */
    checked: boolean;

    /** Callback function triggered when checkbox is changed. */
    onChange?: (isChecked: boolean) => void;

    /** Whether the checkbox is disabled. */
    disabled?: boolean;

    /** The size of the checkbox. */
    size?: IconSize;

    /** The optional ID for the checkbox. */
    id?: string;

    /** An ID to target in frontend tests. */
    dataCy?: string;

    /** Whether the checkbox should appear indeterminate. */
    indeterminate?: boolean;
}

/**
 * The checkbox.
 * @param {ICheckboxProps} param The props used to render the checkbox.
 * @returns The rendered checkbox component.
 */
function Checkbox({
    checked,
    disabled,
    id,
    size = "sm",
    dataCy,
    onChange,
    indeterminate
}: Readonly<ICheckboxProps>): JSX.Element {
    const inputRef = useRef<HTMLInputElement>();

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = !!indeterminate;

            if (indeterminate) {
                inputRef.current.setAttribute("data-indeterminate", "true");
            }
            else {
                inputRef.current.removeAttribute("data-indeterminate");
            }
        }
    }, [indeterminate]);

    return (
        <div>
            <label
                className={`${styles.checkboxControl} ${styles.brand} ${styles.primary}`}
                htmlFor={id}
            >
                <div className={styles.checkboxContainer}>
                    <input
                        ref={inputRef}
                        className={styles[size]}
                        type="checkbox"
                        id={id}
                        data-cy={dataCy}
                        checked={checked}
                        disabled={disabled}
                        onChange={e => onChange?.(e.target.checked)}
                    />
                </div>
            </label>
        </div>
    );
}

export { Checkbox, ICheckboxProps };
